// WHAT: Detailseite für Oura-Aktivitätsdaten mit Score, Schritten, Kalorien, Aktivitätszeiten und Workout-Zusammenfassung
// WHY:  Ermöglicht eine tiefere Analyse der täglichen Aktivität als Ergänzung zum kompakten Dashboard
// DOMAIN: health → oura → pages
// DEPENDENCIES: Optimierte Oura-Hooks (Activity, Workouts), TracQ Card/Badge/Progress, Logging via `debugHealth`
// RISKS:
//   - NOTE: Verlässt sich darauf, dass Lade-/Fehlerzustände bereits in der umgebenden Oura-App behandelt werden
import type { OuraActivity } from "@/domains/health/oura/data/ouraTypes";

import { type FC, useEffect } from "react";

import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { useOptimizedActivity } from "@/domains/health/oura/hooks/useOptimizedActivity";
import { useOptimizedWorkouts } from "@/domains/health/oura/hooks/useOptimizedWorkouts";
import { debugHealth } from "@/domains/logging/utils/debugLog";

type BadgeVariant =
  | "accent"
  | "default"
  | "destructive"
  | "info"
  | "outline"
  | "primary"
  | "success"
  | "warning";

/**
 * WHAT:
 *   Formats duration in seconds to human-readable hours and minutes.
 *
 * WHY:
 *   Provides consistent duration display format across the component.
 *
 * @param {number} [seconds] - Duration in seconds (optional)
 *
 * @returns {string} Formatted string (e.g., "2h 30m") or "N/A" if no duration
 *
 */
const formatDuration = (seconds?: number): string => {
  if (!seconds) {
    return "N/A";
  }
  // NOTE: Convert seconds to hours and minutes
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours)}h ${String(minutes)}m`;
};

/**
 * WHAT:
 *   Formats distance in meters to kilometers with one decimal place.
 *
 * WHY:
 *   Provides consistent distance display format.
 *
 * @param {number} [meters] - Distance in meters (optional)
 *
 * @returns {string} Formatted string (e.g., "5.2 km") or "N/A" if no distance
 *
 */
const formatDistance = (meters?: number): string => {
  if (!meters) {
    return "N/A";
  }
  // NOTE: Convert meters to kilometers with one decimal place
  const km = meters / 1000;
  return `${km.toFixed(1)} km`;
};

/**
 * WHAT:
 *   Calculates rating for steps based on target (default 10,000 steps).
 *
 * WHY:
 *   Provides visual feedback on step goal achievement.
 *
 * @param {number} steps - Current step count
 *
 * @param {number} [target=10000] - Target step count (default 10,000)
 *
 * @returns {{label: string, variant: string}} Object with variant and label for badge display
 *
 */
const getStepsRating = (
  steps: number,
  target = 10_000,
): { label: string; variant: BadgeVariant } => {
  const percentage = (steps / target) * 100;
  if (percentage >= 100) {
    return { label: "Target Reached", variant: "success" };
  }
  if (percentage >= 80) {
    return { label: "Good", variant: "info" };
  }
  if (percentage >= 60) {
    return { label: "Fair", variant: "warning" };
  }
  return { label: "Low", variant: "destructive" };
};

/**
 * WHAT:
 *   Calculates rating for calories based on target (default 650 calories).
 *
 * WHY:
 *   Provides visual feedback on calorie goal achievement.
 *
 * @param {number} calories - Current calorie count
 *
 * @param {number} [target=650] - Target calorie count (default 650)
 *
 * @returns {{label: string, variant: string}} Object with variant and label for badge display
 *
 */
const getCaloriesRating = (
  calories: number,
  target = 650,
): { label: string; variant: BadgeVariant } => {
  const percentage = (calories / target) * 100;
  if (percentage >= 100) {
    return { label: "Target Reached", variant: "success" };
  }
  if (percentage >= 80) {
    return { label: "Good", variant: "info" };
  }
  if (percentage >= 60) {
    return { label: "Fair", variant: "warning" };
  }
  return { label: "Low", variant: "destructive" };
};

/**
 * WHAT:
 *   Calculates rating for activity time based on duration thresholds.
 *
 * WHY:
 *   Provides visual feedback on activity duration achievement.
 *
 * @param {number} minutes - Activity duration in minutes
 *
 * @returns {{label: string, variant: string}} Object with variant and label for badge display
 *
 */
const getActivityTimeRating = (minutes: number): { label: string; variant: BadgeVariant } => {
  if (minutes >= 60) {
    return { label: "Excellent", variant: "success" };
  } // NOTE: >= 1h
  if (minutes >= 30) {
    return { label: "Good", variant: "info" };
  } // NOTE: >= 30min
  if (minutes >= 15) {
    return { label: "Fair", variant: "warning" };
  } // NOTE: >= 15min
  return { label: "Low", variant: "destructive" }; // < 15min
};

/**
 * WHAT:
 *   Calculates rating for inactivity alerts.
 *
 * WHY:
 *   Provides visual feedback on inactivity alert frequency.
 *
 * @param {number} alerts - Number of inactivity alerts
 *
 * @returns {{label: string, variant: string}} Object with variant and label for badge display
 *
 */
const getInactivityAlertsRating = (alerts: number): { label: string; variant: BadgeVariant } => {
  if (alerts === 0) {
    return { label: "Perfect", variant: "success" };
  }
  if (alerts <= 2) {
    return { label: "Good", variant: "info" };
  }
  if (alerts <= 5) {
    return { label: "Fair", variant: "warning" };
  }
  return { label: "Too Many", variant: "destructive" };
};

/**
 * WHAT:
 *   Zeigt eine ausführliche Aufschlüsselung der Aktivitätsdaten eines Tages, inkl. Activity Score,
 *   Schritte, Kalorien, Aktivitätslevel, MET-Minuten, Workouts und qualitativen Insights.
 *
 * WHY:
 *   Dient als „Deep Dive" in die Aktivitätsdaten für Coaching/Analyse über das reine Dashboard hinaus.
 *
 * @returns {JSX.Element | null} Activity detail component
 *
 */
export const ActivityDetail: FC = () => {
  const {
    activity,
    error: activityError,
    loading: activityLoading,
    score: activityScore,
    steps,
  } = useOptimizedActivity();

  const {
    error: workoutsError,
    intensityMinutes,
    loading: workoutsLoading,
    totalCalories,
    workouts,
  } = useOptimizedWorkouts();

  const loading = activityLoading || workoutsLoading;
  const error = activityError ?? workoutsError;

  // Type guard: Ensure activity is defined and typed correctly
  const typedActivity: OuraActivity | undefined = activity;

  // Debug: Log available data
  useEffect(() => {
    if (typedActivity) {
      debugHealth("info", "ActivityDetail", "Activity data loaded", {
        activity: {
          activeCalories: typedActivity.active_calories,
          // Debug: Check all available properties
          allProperties: Object.keys(typedActivity),
          caloriesRating: typedActivity.active_calories
            ? getCaloriesRating(typedActivity.active_calories, typedActivity.target_calories ?? 650)
            : null,
          hasData: true,
          highActivityTime: typedActivity.high_activity_time,
          inactivityAlerts: typedActivity.inactivity_alerts,
          inactivityAlertsRating: typedActivity.inactivity_alerts
            ? getInactivityAlertsRating(typedActivity.inactivity_alerts)
            : null,
          lowActivityTime: typedActivity.low_activity_time,
          mediumActivityTime: typedActivity.medium_activity_time,
          score: typedActivity.score,
          steps: typedActivity.steps,
          stepsRating: typedActivity.steps
            ? getStepsRating(typedActivity.steps, typedActivity.target_steps ?? 10_000)
            : null,
          targetCalories: typedActivity.target_calories,
          targetSteps: typedActivity.target_steps,
          totalCalories: typedActivity.total_calories,
          walkingDistance: typedActivity.equivalent_walking_distance,
        },
      });
    }
  }, [typedActivity]);

  if (loading || error || !typedActivity) {
    return null; // OuraApp handles loading/error states globally
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-neutral-100 mb-2">Activity Score</h2>
        <p className="text-neutral-400">Wie aktiv warst du heute?</p>
      </div>

      {/* Main Score */}
      <Card className="text-center" padding="lg" variant="default">
        <div className="text-6xl font-bold text-neutral-100 mb-2">{activityScore}</div>
        <div className="text-neutral-400 mb-4">von 100</div>

        {/* Score Status */}
        <div className="mt-4">
          {activityScore >= 80 && (
            <Badge className="text-sm" variant="success">
              🟢 Sehr aktiv
            </Badge>
          )}
          {activityScore >= 60 && activityScore < 80 && (
            <Badge className="text-sm" variant="info">
              🔵 Aktiv
            </Badge>
          )}
          {activityScore >= 40 && activityScore < 60 && (
            <Badge className="text-sm" variant="warning">
              🟡 Mäßig
            </Badge>
          )}
          {activityScore < 40 && (
            <Badge className="text-sm" variant="destructive">
              🔴 Inaktiv
            </Badge>
          )}
        </div>
      </Card>

      {/* Steps and Calories */}
      <Card padding="lg" variant="default">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Schritte & Kalorien</h3>
        <div className="grid gap-4">
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Schritte</span>
            <Badge
              className="font-medium"
              variant={
                steps
                  ? getStepsRating(steps, typedActivity.target_steps ?? 10_000).variant
                  : "default"
              }
            >
              {steps ? steps.toLocaleString() : 0} /{" "}
              {typedActivity.target_steps ? typedActivity.target_steps.toLocaleString() : 10_000}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Aktive Kalorien</span>
            <Badge
              className="font-medium"
              variant={
                typedActivity.active_calories
                  ? getCaloriesRating(
                      typedActivity.active_calories,
                      typedActivity.target_calories ?? 650,
                    ).variant
                  : "default"
              }
            >
              {typedActivity.active_calories?.toFixed(0) ?? 0} /{" "}
              {typedActivity.target_calories ?? 650} cal
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Gesamtkalorien</span>
            <Badge className="font-medium" variant="info">
              {typedActivity.total_calories?.toFixed(0) ?? 0} cal
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Gehstrecke</span>
            <Badge className="font-medium" variant="info">
              {formatDistance(typedActivity.equivalent_walking_distance)}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Activity Levels */}
      <Card padding="lg" variant="default">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Aktivitätslevel</h3>
        <div className="grid gap-4">
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Hohe Aktivität</span>
            <Badge
              className="font-medium"
              variant={
                typedActivity.high_activity_time
                  ? getActivityTimeRating(typedActivity.high_activity_time / 60).variant
                  : "default"
              }
            >
              {formatDuration(typedActivity.high_activity_time)}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Mittlere Aktivität</span>
            <Badge
              className="font-medium"
              variant={
                typedActivity.medium_activity_time
                  ? getActivityTimeRating(typedActivity.medium_activity_time / 60).variant
                  : "default"
              }
            >
              {formatDuration(typedActivity.medium_activity_time)}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Niedrige Aktivität</span>
            <Badge
              className="font-medium"
              variant={
                typedActivity.low_activity_time
                  ? getActivityTimeRating(typedActivity.low_activity_time / 60).variant
                  : "default"
              }
            >
              {formatDuration(typedActivity.low_activity_time)}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Inaktivitäts-Alerts</span>
            <Badge
              className="font-medium"
              variant={
                typedActivity.inactivity_alerts
                  ? getInactivityAlertsRating(typedActivity.inactivity_alerts).variant
                  : "default"
              }
            >
              {typedActivity.inactivity_alerts ?? 0}
            </Badge>
          </div>
        </div>
      </Card>

      {/* MET Minutes */}
      <Card padding="lg" variant="default">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">MET-Minuten</h3>
        <div className="grid gap-4">
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Durchschnittliche MET-Minuten</span>
            <Badge className="font-medium" variant="info">
              {typedActivity.average_met_minutes?.toFixed(2) ?? 0}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Sedentary MET-Minuten</span>
            <Badge className="font-medium" variant="default">
              {typedActivity.sedentary_met_minutes ?? 0}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Low Activity MET-Minuten</span>
            <Badge className="font-medium" variant="warning">
              {typedActivity.low_activity_met_minutes ?? 0}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Medium Activity MET-Minuten</span>
            <Badge className="font-medium" variant="info">
              {typedActivity.medium_activity_met_minutes ?? 0}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">High Activity MET-Minuten</span>
            <Badge className="font-medium" variant="success">
              {typedActivity.high_activity_met_minutes ?? 0}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Training Load */}
      {typedActivity.training_load !== undefined && (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Training Load</h3>
          <div className="flex items-center justify-between">
            <span className="text-neutral-300">Training Load</span>
            <Badge
              className="font-medium"
              variant={(() => {
                if (typedActivity.training_load >= 100) {
                  return "success";
                }
                if (typedActivity.training_load >= 50) {
                  return "warning";
                }
                return "destructive";
              })()}
            >
              {typedActivity.training_load.toFixed(1)}
            </Badge>
          </div>
        </Card>
      )}

      {/* Workout Summary */}
      {workouts.length > 0 && (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Workouts heute</h3>
          <div className="space-y-3">
            {workouts.map((workout) => {
              const startTime = workout.start_datetime ?? workout.start_time;
              const endTime = workout.end_datetime ?? workout.end_time;

              if (!startTime || !endTime) {
                return null;
              }

              const start = new Date(startTime);
              const end = new Date(endTime);
              const duration = Math.round((end.getTime() - start.getTime()) / (1000 * 60));

              return (
                <div
                  className="flex items-center justify-between p-3 bg-transparent-800 rounded-lg"
                  key={workout.id}
                >
                  <div className="flex-1">
                    <div className="font-medium text-neutral-200">
                      {workout.activity ?? workout.label ?? "Aktivität"}
                    </div>
                    <div className="text-sm text-neutral-400">
                      {start.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      - {duration}min
                    </div>
                  </div>
                  <div className="text-right">
                    {workout.calories ? (
                      <div className="font-medium text-neutral-200">
                        {workout.calories.toFixed(0)} cal
                      </div>
                    ) : null}
                    {workout.intensity ? (
                      <div className="text-sm text-neutral-400">{workout.intensity}</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total Calories from Workouts */}
          {totalCalories > 0 && (
            <div className="mt-4 pt-4 border-t border-neutral-600">
              <div className="flex items-center justify-between">
                <span className="text-neutral-300 font-medium">Gesamtkalorien (Workouts)</span>
                <Badge className="font-bold" variant="warning">
                  {totalCalories.toFixed(0)} cal
                </Badge>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Intensity Minutes */}
      {Object.keys(intensityMinutes).length > 0 && (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Intensitäts-Minuten</h3>
          <div className="space-y-2">
            {Object.entries(intensityMinutes).map(([intensity, minutes]) => (
              <div className="flex items-center justify-between" key={intensity}>
                <span className="text-neutral-300 capitalize">{intensity}</span>
                <Badge className="font-medium" variant="default">
                  {minutes} min
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Insights */}
      <Card padding="lg" variant="default">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Insights</h3>
        <div className="space-y-3 text-neutral-300">
          {activityScore >= 80 && <p>🏃‍♂️ Sehr aktiv! Du hast dein tägliches Ziel übertroffen.</p>}
          {activityScore >= 60 && activityScore < 80 && (
            <p>🚶‍♂️ Gut aktiv. Du bist auf dem richtigen Weg!</p>
          )}
          {activityScore >= 40 && activityScore < 60 && (
            <p>🔄 Moderate Aktivität. Versuche mehr Bewegung zu integrieren.</p>
          )}
          {activityScore < 40 && <p>💤 Wenig aktiv. Zeit für einen Spaziergang?</p>}

          {steps && steps < 8000 ? (
            <p>👣 Du hast weniger als 8.000 Schritte gemacht. Versuche mehr zu gehen!</p>
          ) : null}

          {typedActivity.inactivity_alerts && typedActivity.inactivity_alerts > 5 ? (
            <p>⚠️ Viele Inaktivitäts-Alerts. Stehe öfter auf und bewege dich!</p>
          ) : null}
        </div>
      </Card>
    </div>
  );
};

// Named export only - no default export
